import { computed, type ComputedRef, type Ref } from "vue";
import type { Locale } from "~/types/locale";
import {
	defaultSiteFolder,
	isTruthy,
	trimFolder
} from "~/utils/storyblok/siteFolder";

export interface StoryblokSiteFolder {
	/** Folder used as the primary Storyblok request prefix. */
	folder: ComputedRef<string | null>;
	/** Folder used as the second-attempt prefix when the primary 404s. */
	fallbackFolder: ComputedRef<string | null>;
	/**
	 * True when the root fallback is off — Storyblok requests stay within the
	 * folder chain (folder → fallbackFolder) and never fall back to the shared
	 * space root. Enabled either deploy-wide via `NORCE_STORYBLOK_STRICT` or
	 * per-locale via `rootFallback: false`.
	 */
	strict: ComputedRef<boolean>;
	/**
	 * True when the active locale maintains its own blog content (explicit
	 * `isLocaleBlog: true` on the locale, or it is the default locale).
	 * Blog composables read this to decide whether to include the
	 * active locale's `storyblokFolder` in the lookup chain or skip
	 * straight to the default site's folder.
	 */
	hasLocaleBlog: ComputedRef<boolean>;
}

// Root fallback is enabled unless a locale explicitly opts out with
// `rootFallback: false` (boolean `false`, or the string "false" from env
// JSON). Default (unset / true) keeps the legacy root attempt.
const isRootFallbackDisabled = (value: unknown): boolean =>
	value === false ||
	(typeof value === "string" && value.trim().toLowerCase() === "false");

export const useStoryblokSiteFolder = (): StoryblokSiteFolder => {
	const config = useRuntimeConfig();
	const nuxtApp = useNuxtApp();
	// Pull localeProperties from the i18n composer exposed on $i18n rather
	// than via useI18n(). useI18n() requires being called at the top of a
	// component setup and throws MUST_BE_CALL_SETUP_TOP (code 26) when
	// invoked from a watcher callback (e.g. after a locale switch).
	// nuxtApp.$i18n is set by the i18n plugin and accessible from any
	// Nuxt context, including watchers and async chains.
	const localeProperties = (nuxtApp.$i18n as { localeProperties: Ref<unknown> })
		.localeProperties;

	const locales = (config.public?.norce?.locales as Locale[] | undefined) ?? [];
	const envFolder = trimFolder(config.public?.storyblokFolder);
	const envFallbackFolder = trimFolder(config.public?.storyblokFallbackFolder);
	// Deploy-level strict switch (NORCE_STORYBLOK_STRICT). Turns off the
	// root fallback for the whole deploy, independent of per-locale
	// `rootFallback`. Lets a single-site deploy (env CONTENT_FOLDER +
	// CONTENT_FALLBACK_FOLDER) stay strict without repeating the flag on
	// every locale.
	const envStrict = config.public?.storyblokStrict === true;

	const defaultFolder = defaultSiteFolder(locales, envFolder);
	const isMultiSite = locales.some(
		(l) => !isTruthy(l.isDefault) && Boolean(trimFolder(l.storyblokFolder))
	);

	// i18n's localeProperties only carries the standard fields (code, iso,
	// file, alternatives). Custom fields like storyblokFolder are NOT
	// propagated through i18n config (passing them there breaks $i18n
	// plugin init). Instead, look up the full Locale from runtime config
	// by matching the active code.
	const resolveActive = (): Partial<Locale> | undefined => {
		const fromI18n = localeProperties.value as Partial<Locale> | undefined;
		if (!fromI18n?.code) return fromI18n;
		return locales.find((l) => l.code === fromI18n.code) ?? fromI18n;
	};

	const folder = computed<string | null>(() => {
		const active = resolveActive();
		// 1. Explicit folder on the active locale wins.
		const fromLocale = trimFolder(active?.storyblokFolder);
		if (fromLocale) return fromLocale;
		// 2. Anyone else (default locale or locale without own folder) reads
		//    from the default site's folder.
		return defaultFolder;
	});

	const fallbackFolder = computed<string | null>(() => {
		const active = resolveActive();
		const ownFolder = trimFolder(active?.storyblokFolder);
		// Non-default locale with its own folder → fall back to the default
		// site's folder. Exactly one extra request, no root-fallback.
		if (ownFolder && ownFolder !== defaultFolder) {
			return defaultFolder;
		}
		// Default locale (or locale already reading from default) in
		// multi-site mode → no fallback.
		if (isMultiSite) return null;
		// Single-folder legacy deploys keep the env fallback chain.
		return envFallbackFolder;
	});

	// The legacy root-fallback (the no-folder attempt at the space root) is
	// ON by default — existing content still lives at the root and the root
	// attempt keeps those pages serving during the migration window. A
	// locale opts OUT by setting `rootFallback: false`, which makes its
	// fetches strict: own folder → default folder, never the shared root.
	// That avoids the wasted root 404 and cross-site content bleed once a
	// site is fully migrated. Explicit legacy array-fallbacks
	// (`fetchStory(["a", "b"])`) bypass `strict` and are unaffected.
	//
	// The deploy-level `NORCE_STORYBLOK_STRICT` env (envStrict) forces strict
	// for every locale — preferred for a single-site deploy so you don't
	// repeat `rootFallback: false` across LOCALES.
	const strict = computed<boolean>(
		() => envStrict || isRootFallbackDisabled(resolveActive()?.rootFallback)
	);

	const hasLocaleBlog = computed<boolean>(() => {
		const active = resolveActive();
		// Default locale always owns blog content by definition — it is
		// the canonical source other locales translate from.
		if (isTruthy(active?.isDefault)) return true;
		return isTruthy(active?.isLocaleBlog);
	});

	return { folder, fallbackFolder, strict, hasLocaleBlog };
};
